import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

/** The pages `show_form` opens, as the model names them (and as each page reports itself). */
const VIEWS = ['list', 'builder', 'submissions'] as const
type FormsView = (typeof VIEWS)[number]

/** Matches the server's cap on a form id in the page context. */
const MAX_ID = 64

/** A page reads its form before it reports it; past this the page is shown without a claim. */
const SHOW_WAIT_MS = 4_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

interface ShowRequest {
  view: FormsView
  formId?: string
}

function readRequest(input: unknown): ShowRequest | { error: string } {
  const raw = (input ?? {}) as { view?: unknown; form_id?: unknown }
  const view = raw.view === undefined ? 'list' : raw.view

  if (!VIEWS.includes(view as FormsView)) {
    return { error: "Invalid view: expected 'list', 'builder' or 'submissions'." }
  }

  const id = typeof raw.form_id === 'string' ? raw.form_id.trim() : ''

  if (raw.form_id !== undefined && (typeof raw.form_id !== 'string' || id.length > MAX_ID)) {
    return { error: 'Invalid form_id.' }
  }

  if (view !== 'list' && !id) return { error: `The '${view as string}' view needs a form_id.` }

  return { view: view as FormsView, ...(id && view !== 'list' ? { formId: id } : {}) }
}

function pathOf(req: ShowRequest): string {
  if (req.view === 'builder') return `/forms/builder/${encodeURIComponent(req.formId!)}`
  if (req.view === 'submissions') return `/forms/${encodeURIComponent(req.formId!)}/submissions`

  return '/forms'
}

/** True once the forms page on screen reports the requested view (and form), loaded. */
function showing(req: ShowRequest): boolean {
  const handle = getAssistantSurface()
  if (handle?.kind !== 'forms') return false

  const ctx = handle.readContext().forms
  if (ctx.view !== req.view || ctx.loading) return false

  return !req.formId || ctx.form?.id === req.formId
}

async function awaitShowing(req: ShowRequest): Promise<boolean> {
  const deadline = Date.now() + SHOW_WAIT_MS

  for (;;) {
    if (showing(req)) return true
    if (Date.now() >= deadline) return false
    await delay(POLL_MS)
  }
}

/**
 * `show_form`. `refusal` is the route manifest's answer for `/forms` for this caller, resolved by
 * `executeClientTool` (which owns the resolver), or null when allowed.
 */
export async function executeShowForm(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const req = readRequest(input)
  if ('error' in req) return req

  if (refusal) return { refused: refusal }

  const target = pathOf(req)
  const onTargetPage = deps.currentRoute() === target
  const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

  if (!onTargetPage && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  // Showing what is already shown must not add a Back entry.
  if (onTargetPage) deps.navigate(target, { replace: true })
  else deps.navigate(target)

  const formId = req.formId ? { form_id: req.formId } : {}

  return (await awaitShowing(req))
    ? { shown: true, view: req.view, ...formId }
    : { shown: true, note: 'The Forms page did not report this view in time; it is open but may still be loading.' }
}
